import { ILoginDto } from "@kuika/kuika-cl-model";
import Form, { Field, FormInstance, useForm } from "rc-field-form";
import * as React from "react";
import { getRememberedAccountInitials, IRememberedAccount } from "../../auth/remembered-accounts";
import { KNavigator } from "../../shared/hoc/with-history";
import { getIdentitySeed, KuikaAvatar } from "../ui-kit/kuika-avatar/kuika-avatar";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import { toastService } from "../ui-kit/kuika-toast/toast-service";
import { GoogleLogin } from "./google-login";
import { SignInDivider, SignInPasswordField } from "./sign-in-fields";
import { goToForgotPassword, IPasswordSignIn, usePasswordSignIn } from "./use-password-sign-in";

export interface IRememberedAccountSignInProps {
  account: IRememberedAccount;
  isLoginHTML: boolean;
  navigator: KNavigator;
  isGoogleAvailable: boolean;
  onSwitchAccount: () => void;
}

/**
 * Signing in as an account this device remembers: the address is known, so only the password — or
 * the Google button, when that is how this person signed in last time — is asked for.
 */
export const RememberedAccountSignIn = ({
  account,
  isLoginHTML,
  navigator,
  isGoogleAvailable,
  onSwitchAccount
}: IRememberedAccountSignInProps): React.ReactElement => {
  const [form]: [FormInstance<ILoginDto>] = useForm<ILoginDto>();
  const signIn: IPasswordSignIn = usePasswordSignIn({ form, isLoginHTML, navigator });
  const [isGoogleLoading, setIsGoogleLoading] = React.useState<boolean>(false);

  // A Google account on an origin where Google is off (on-prem, customer domains) still gets a
  // working primary action: the password form.
  const leadsWithGoogle: boolean = account.method === "google" && isGoogleAvailable;
  // Switching away mid-request would unmount the form while its answer is still coming — and that
  // answer would sign in (and remember) the account the person just left.
  const isBusy: boolean = signIn.isSubmitting || isGoogleLoading;

  const onGoogleSuccess: () => void = React.useCallback((): void => {
    setIsGoogleLoading(true);
  }, []);

  const onGoogleFailure: (error?: string) => void = React.useCallback((error?: string): void => {
    setIsGoogleLoading(false);
    toastService.error(error || "Something went wrong.");
  }, []);

  const onForgotPassword: () => void = React.useCallback((): void => {
    goToForgotPassword(isLoginHTML, navigator);
  }, [isLoginHTML, navigator]);

  const googleButton: React.ReactNode = isGoogleAvailable ? (
    <GoogleLogin
      loading={isGoogleLoading}
      routing={isLoginHTML ? "static" : "dynamic"}
      type="login"
      hint={account.email}
      emphasis={leadsWithGoogle ? "primary" : "default"}
      onSuccess={onGoogleSuccess}
      onFailure={onGoogleFailure}
    />
  ) : null;

  const passwordForm: React.ReactNode = (
    <Form
      form={form}
      initialValues={{ username: account.email }}
      onFinish={signIn.submit}
      onValuesChange={signIn.clearCredentialsError}
      className="auth-page__credentials"
    >
      {/* Password managers choose the saved credential by the username field. The address is
          already known here, so it rides along hidden instead of being asked for again. */}
      <Field name="username">
        <input type="email" autoComplete="username" hidden readOnly />
      </Field>

      <SignInPasswordField
        disabled={isGoogleLoading}
        autoFocus={!leadsWithGoogle}
        onForgotPassword={onForgotPassword}
      />

      {signIn.emailToResend !== undefined && (
        <KuikaButton
          type="text"
          htmlType="button"
          onClick={signIn.resendVerificationEmail}
          loading={signIn.isResending}
          data-testid="resendVerificationButton"
        >
          Resend verification email
        </KuikaButton>
      )}

      <KuikaButton
        data-testid="signInButton"
        htmlType="submit"
        size="lg"
        type={leadsWithGoogle ? "secondary" : "primary"}
        color={leadsWithGoogle ? "neutral" : "default"}
        loading={signIn.isSubmitting}
        disabled={isGoogleLoading}
        className="kuika-width-full"
      >
        {leadsWithGoogle ? "Sign in with password" : "Sign in"}
      </KuikaButton>
    </Form>
  );

  return (
    <div className="auth-page__panel" data-testid="rememberedAccountSignIn">
      <div className="auth-page__account-header">
        <KuikaAvatar
          size={48}
          src={account.photoUrl}
          seed={getIdentitySeed({ firstName: account.firstName, lastName: account.lastName, username: account.email })}
        >
          {getRememberedAccountInitials(account)}
        </KuikaAvatar>
        <div className="auth-page__heading">
          <h1 className="auth-page__title">
            {account.firstName.length > 0 ? `Welcome back, ${account.firstName}` : "Welcome back"}
          </h1>
          <div className="auth-page__account-identity">
            <span className="auth-page__account-email" data-testid="rememberedAccountEmail">
              {account.email}
            </span>
            <KuikaButton
              type="text"
              size="sm"
              htmlType="button"
              onClick={onSwitchAccount}
              disabled={isBusy}
              className="auth-page__inline-action"
              data-testid="switchAccountButton"
            >
              Switch account
            </KuikaButton>
          </div>
        </div>
      </div>

      {leadsWithGoogle ? (
        <>
          {googleButton}
          <SignInDivider label="or use your password" />
          {passwordForm}
        </>
      ) : (
        <>
          {passwordForm}
          {googleButton && (
            <>
              <SignInDivider />
              {googleButton}
            </>
          )}
        </>
      )}
    </div>
  );
};
